// Simple map refresh utilities

// Simple data change detection
export class DataChangeDetector {
  constructor() {
    this.cache = new Map();
  }

  generateHashKey(path, locations, options = {}) {
    const pathStr = Array.isArray(path) ? path.join(',') : '';
    const locationsStr = Array.isArray(locations) ? 
      locations.map(loc => `${loc.id}:${loc.latitude}:${loc.longitude}`).join('|') : '';
    return `${pathStr}#${locationsStr}`;
  }

  hasDataChanged(cacheKey, dataHash) {
    const previousHash = this.cache.get(cacheKey);
    const changed = previousHash !== dataHash;
    
    if (changed) {
      this.cache.set(cacheKey, dataHash);
    }
    
    return changed;
  }

  clearCache() {
    this.cache.clear();
  }
}

// Simple GeoJSON cache
export class GeoJSONCache {
  constructor(maxSize = 20) {
    this.cache = new Map();
    this.maxSize = maxSize;
  }

  get(key) {
    return this.cache.get(key);
  }

  set(key, geoJSON) {
    if (this.cache.size >= this.maxSize) {
      const firstKey = this.cache.keys().next().value;
      this.cache.delete(firstKey);
    }
    this.cache.set(key, geoJSON);
  }

  has(key) {
    return this.cache.has(key);
  }

  clear() {
    this.cache.clear();
  }
}

// Smart refresh manager
export class SmartRefreshManager {
  constructor() {
    this.changeDetector = new DataChangeDetector();
    this.geoJSONCache = new GeoJSONCache();
    this.updateQueue = [];
    this.isProcessing = false;
  }

  // Check if refresh needed
  shouldRefreshPath(pathType, path, locations, options = {}) {
    const cacheKey = `${pathType}-path`;
    const dataHash = this.changeDetector.generateHashKey(path, locations, options);
    const needsRefresh = this.changeDetector.hasDataChanged(cacheKey, dataHash);

    // Check for cached GeoJSON
    let geoJSON = null;
    if (!needsRefresh) {
      geoJSON = this.geoJSONCache.get(dataHash);
    }

    return {
      needsRefresh: needsRefresh || !geoJSON,
      useCache: !needsRefresh && geoJSON !== null,
      cachedGeoJSON: geoJSON,
      dataHash
    };
  }

  // Convert path to GeoJSON
  pathToGeoJSON(path, locations, pathType, dataHash) {
    // Check cache first
    const cached = this.geoJSONCache.get(dataHash);
    if (cached) {
      return cached;
    }

    // Generate new GeoJSON
    const coordinates = path.map(id => {
      const location = locations.find(loc => loc.id === id);
      if (!location) {

        return null;
      }
      return [location.longitude, location.latitude];
    }).filter(Boolean);

    const geoJSON = {
      type: 'Feature',
      properties: { 
        pathType,
        timestamp: Date.now(),
        pointCount: coordinates.length
      },
      geometry: { 
        type: 'LineString', 
        coordinates 
      }
    };

    // Cache the result
    this.geoJSONCache.set(dataHash, geoJSON);
    
    return geoJSON;
  }

  // Queue update
  queueUpdate(updateFn, priority = 1) {
    this.updateQueue.push({ updateFn, priority, timestamp: Date.now() });
    this.updateQueue.sort((a, b) => a.priority - b.priority);
    
    if (!this.isProcessing) {
      this.processQueue();
    }
  }

  // Process update queue
  async processQueue() {
    if (this.isProcessing || this.updateQueue.length === 0) {
      return;
    }

    this.isProcessing = true;

    // Process in batches
    const batchSize = 3;
    while (this.updateQueue.length > 0) {
      const batch = this.updateQueue.splice(0, batchSize);
      
      // Execute batch
      await Promise.all(
        batch.map(({ updateFn }) => {
          try {
            return Promise.resolve(updateFn());
          } catch (error) {
            console.warn('Update function failed:', error);
            return Promise.resolve();
          }
        })
      );

      // Small delay to prevent blocking
      if (this.updateQueue.length > 0) {
        await new Promise(resolve => setTimeout(resolve, 16)); // ~60fps
      }
    }

    this.isProcessing = false;

  }

  // Clear all caches
  invalidateAll() {
    this.changeDetector.clearCache();
    this.geoJSONCache.clear();

  }

  // Get stats
  getStats() {
    return {
      cacheSize: this.changeDetector.cache.size,
      geoJSONCacheSize: this.geoJSONCache.cache.size,
      queuePending: this.updateQueue.length
    };
  }
}

// Export instance
export const smartRefreshManager = new SmartRefreshManager();
